import React, { useEffect, useState } from "react";
import { FaFileCsv, FaEdit, FaTrash, FaUpload, FaEye } from "react-icons/fa";
import { CSVLink } from "react-csv";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import StaffDetailsForm from "./StaffDetailsForm";

const CompanyList = () => {
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const companiesPerPage = 5;

  const [isEditingCompanyModalOpen, setIsEditingCompanyModalOpen] = useState(false);
  const [isAddingBeneficiaryModalOpen, setIsAddingBeneficiaryModalOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [companyDetails, setCompanyDetails] = useState({});
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewedCompany, setViewedCompany] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const token = localStorage.getItem("authToken");

        const response = await fetch(
          "https://api.elthiumhealth.com/api/admin/companies",
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const result = await response.json();

        if (response.ok) {
          setCompanies(result.companies || []);
        } else {
          console.error("Error fetching companies:", result.message);
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };

    fetchCompanies();
  }, []);

  const handleViewDetails = async (companyId) => {
    try {
      const token = localStorage.getItem("authToken");

      const response = await fetch(
        "https://api.elthiumhealth.com/api/admin/singlecompany",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            companyId: companyId,
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setViewedCompany(result.company);
        setIsViewModalOpen(true);
      } else {
        Swal.fire({
          title: "Error!",
          text: `Error fetching company details: ${result.message || "Unknown error"
            }`,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Error fetching company details:", error);

      Swal.fire({
        title: "Error!",
        text: "Failed to fetch company details",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination Logic
  const indexOfLastCompany = currentPage * companiesPerPage;
  const indexOfFirstCompany = indexOfLastCompany - companiesPerPage;
  const currentCompanies = filteredCompanies.slice(indexOfFirstCompany, indexOfLastCompany);
  const totalPages = Math.ceil(filteredCompanies.length / companiesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  const headers = [
    { label: "SI No", key: "index" },
    { label: "Name", key: "name" },
    { label: "Industry", key: "companyType" },
    { label: "Number of Branches", key: "noOfBranches" },
    { label: "Empanelment", key: "registrationDate" },
    { label: "Contract Period", key: "contractPeriod" },
    { label: "Renewal Date", key: "renewalDate" },
    { label: "Insurance Partner", key: "insuranceBroker" },
    { label: "Actions", key: "actions" },
    { label: "View Employee", key: "viewEmployee" },
    { label: "Add Beneficiary", key: "addBeneficiary" }
  ];

  // Edit Handler
  const handleEdit = (id) => {
    const company = companies.find(c => c._id === id);
    if (company) {
      setCompanyDetails(company);
      setSelectedCompanyId(id);
      setIsEditingCompanyModalOpen(true);
      Swal.fire({
        title: 'Editing Company',
        text: `You are editing the company: ${company.name}`,
        icon: 'info',
        confirmButtonText: 'OK',
      });
    }
  };

  // Bulk Import Handler
  const handleBulkImport = async (event) => {
    const file = event.target.files[0];

    if (!file) return;

    const token = localStorage.getItem("authToken");
    const formData = new FormData();

    formData.append("file", file);

    try {
      const response = await fetch(
        "https://api.elthiumhealth.com/api/admin/import-companies",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (response.ok) {
        Swal.fire({
          title: "Success!",
          text: "Data imported successfully!",
          icon: "success",
          confirmButtonText: "OK",
        });

        setCompanies((prevCompanies) => [
          ...prevCompanies,
          ...(result.data || []),
        ]);
      } else {
        Swal.fire({
          title: "Error!",
          text: `Error importing data: ${result.error || result.message || "Unknown error"}`,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Bulk import error:", error);

      Swal.fire({
        title: "Error!",
        text: "Failed to upload file.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      // Allow selecting the same file again
      event.target.value = "";
    }
  };

  // Delete Handler
  const handleDelete = async (id) => {
    const { isConfirmed } = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it",
    });

    if (!isConfirmed) return;

    try {
      const token = localStorage.getItem("authToken");

      const response = await fetch(
        "https://api.elthiumhealth.com/api/admin/delete-company",
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            companyId: id,
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        await Swal.fire({
          title: "Deleted!",
          text: "Company deleted successfully!",
          icon: "success",
          confirmButtonText: "OK",
        });

        setCompanies((prevCompanies) =>
          prevCompanies.filter((company) => company._id !== id)
        );
      } else {
        Swal.fire({
          title: "Failed!",
          text: `Failed to delete company: ${result.message || "Unknown error"
            }`,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Delete company error:", error);

      Swal.fire({
        title: "Error!",
        text: "There was an error deleting the company.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const closeCompanyEditModal = () => {
    setIsEditingCompanyModalOpen(false);
    setSelectedCompanyId(null);
    setCompanyDetails({});
  };

  const closeBeneficiaryModal = () => {
    setIsAddingBeneficiaryModalOpen(false);
    setSelectedCompanyId(null);
    setCompanyDetails({});
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setViewedCompany(null);
  };

  const handleViewStaff = (companyId) => {
    navigate(`/stafflist?companyId=${companyId}`);
  };

  const handleAddBeneficiary = (companyId) => {
    setSelectedCompanyId(companyId);
    setIsAddingBeneficiaryModalOpen(true);
  };

  // Handle Submit Edit
  const handleSubmitEdit = async (e) => {
    e.preventDefault();

    if (!selectedCompanyId) {
      Swal.fire("Error", "Company ID is missing.", "error");
      return;
    }

    try {
      const token = localStorage.getItem("authToken");

      // Create FormData for file uploads and fields
      const formData = new FormData();

      // Move company ID from URL into request body
      formData.append("companyId", selectedCompanyId);

      // Append basic fields
      formData.append("name", companyDetails.name || "");
      formData.append("companyType", companyDetails.companyType || "");
      formData.append("assignedBy", companyDetails.assignedBy || "");
      formData.append("registrationDate", companyDetails.registrationDate || "");
      formData.append("contractPeriod", companyDetails.contractPeriod || "");
      formData.append("renewalDate", companyDetails.renewalDate || "");
      formData.append("insuranceBroker", companyDetails.insuranceBroker || "");
      formData.append("email", companyDetails.email || "");
      formData.append("phone", companyDetails.phone || "");
      formData.append("gstNumber", companyDetails.gstNumber || "");
      formData.append("companyStrength", companyDetails.companyStrength || "");
      formData.append("country", companyDetails.country || "");
      formData.append("state", companyDetails.state || "");
      formData.append("city", companyDetails.city || "");
      formData.append("pincode", companyDetails.pincode || "");

      if (companyDetails.password) {
        formData.append("password", companyDetails.password);
      }

      // Append arrays as JSON strings
      if (companyDetails.contactPerson) {
        formData.append(
          "contactPerson",
          JSON.stringify(companyDetails.contactPerson)
        );
      }

      if (companyDetails.branches) {
        formData.append(
          "branches",
          JSON.stringify(companyDetails.branches)
        );
      }

      if (companyDetails.diagnostics) {
        formData.append(
          "diagnostic",
          JSON.stringify(companyDetails.diagnostics)
        );
      }

      const response = await fetch(
        "https://api.elthiumhealth.com/api/admin/update-company",
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (response.ok) {
        await Swal.fire({
          title: "Success!",
          text: "Company details updated successfully!",
          icon: "success",
          confirmButtonText: "OK",
        });

        setCompanies((prevCompanies) =>
          prevCompanies.map((company) =>
            company._id === selectedCompanyId
              ? result.company
              : company
          )
        );

        closeCompanyEditModal();
      } else {
        Swal.fire({
          title: "Failed!",
          text: result.message || "Failed to update company details",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Error updating company:", error);

      Swal.fire({
        title: "Error!",
        text: "There was an error updating the company.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  // Helper component for table rows
  const TableRow = ({ label, value }) => (
    <tr>
      <td className="px-4 py-2 whitespace-nowrap font-medium text-gray-900 bg-gray-50">
        {label}
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        {value || <span className="text-gray-400">Not provided</span>}
      </td>
    </tr>
  );

  // ==================== SIMPLIFIED VIEW COMPANY MODAL ====================
  const ViewCompanyModal = () => (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 w-[95%] max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl relative">
        <button
          className="absolute top-2 right-3 text-gray-500 hover:text-red-500 text-xl font-bold"
          onClick={closeViewModal}
        >
          ×
        </button>
        <h3 className="text-2xl font-bold mb-6 text-center text-blue-600">Company Overview</h3>

        {/* ==================== BASIC INFORMATION ==================== */}
        <div className="mb-8">
          <h4 className="text-lg font-semibold mb-3 border-b pb-2">Basic Information</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border">
              <tbody className="divide-y divide-gray-200">
                <TableRow label="Name" value={viewedCompany?.name} />
                <TableRow label="Email" value={viewedCompany?.email} />
                <TableRow label="Phone" value={viewedCompany?.phone} />
                <TableRow label="Industry Type" value={viewedCompany?.companyType} />
                <TableRow label="Number of Branches" value={viewedCompany?.noOfBranches} />
                <TableRow label="Contact Person" value={viewedCompany?.assignedBy} />
                <TableRow label="Empanelment" value={viewedCompany?.registrationDate?.slice(0, 10)} />
                <TableRow label="Contract Period" value={viewedCompany?.contractPeriod} />
                <TableRow label="Renewal Date" value={viewedCompany?.renewalDate?.slice(0, 10)} />
                <TableRow label="Insurance Partner" value={viewedCompany?.insuranceBroker} />
                <TableRow label="EMP Head Count" value={viewedCompany?.companyStrength} />
                <TableRow label="Country" value={viewedCompany?.country} />
                <TableRow label="State" value={viewedCompany?.state} />
                <TableRow label="City" value={viewedCompany?.city} />
                <TableRow label="Pincode" value={viewedCompany?.pincode} />
                <TableRow label="GST Number" value={viewedCompany?.gstNumber} />

                {/* Company Image */}
                {viewedCompany?.image && (
                  <tr>
                    <td className="font-semibold px-4 py-2">Image</td>
                    <td className="px-4 py-2">
                      <img
                        src={`https://api.elthiumhealth.com${viewedCompany.image}`}
                        alt="Company Logo"
                        className="w-32 h-32 object-contain border rounded"
                      />
                    </td>
                  </tr>
                )}

                {/* Documents */}
                {viewedCompany?.documents?.length > 0 && (
                  <tr>
                    <td className="font-semibold px-4 py-2">Documents</td>
                    <td className="px-4 py-2 space-y-2">
                      {viewedCompany.documents.map((doc, idx) => (
                        <a
                          key={idx}
                          href={`https://api.elthiumhealth.com${doc}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline block"
                        >
                          Document {idx + 1}
                        </a>
                      ))}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ==================== CONTACT PERSONS ==================== */}
        {viewedCompany?.contactPerson?.length > 0 && (
          <div className="mb-8">
            <h4 className="text-lg font-semibold mb-3 border-b pb-2">
              Contact Persons ({viewedCompany.contactPerson.length})
            </h4>
            <div className="space-y-4">
              {viewedCompany.contactPerson.map((person, idx) => (
                <div key={idx} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                  <h5 className="font-semibold mb-3 text-lg">Contact Person {idx + 1}</h5>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border">
                      <tbody>
                        <TableRow label="Name" value={person.name} />
                        <TableRow label="Designation" value={person.designation} />
                        <TableRow label="Gender" value={person.gender} />
                        <TableRow label="Email" value={person.email} />
                        <TableRow label="Phone" value={person.phone} />
                        <TableRow label="Address - Country" value={person.address?.country} />
                        <TableRow label="Address - State" value={person.address?.state} />
                        <TableRow label="Address - City" value={person.address?.city} />
                        <TableRow label="Address - Pincode" value={person.address?.pincode} />
                        <TableRow label="Address - Street" value={person.address?.street} />
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ==================== BRANCHES ==================== */}
        {viewedCompany?.branches?.length > 0 && (
          <div className="mb-8">
            <h4 className="text-lg font-semibold mb-3 border-b pb-2">
              Branches ({viewedCompany.branches.length})
            </h4>
            <div className="space-y-4">
              {viewedCompany.branches.map((branch, branchIndex) => (
                <div key={branch._id || branchIndex} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-center mb-3">
                    <h5 className="font-semibold text-lg text-gray-800">
                      Branch {branchIndex + 1}: {branch.branchName}
                    </h5>
                    <span className={`px-2 py-1 text-xs rounded ${branch.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                      }`}>
                      {branch.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <div>
                      <table className="w-full border">
                        <tbody>
                          <TableRow label="Branch Code" value={branch.branchCode} />
                          <TableRow label="Branch Head" value={branch.branchHead} />
                          <TableRow label="Branch Email" value={branch.email} />
                          <TableRow label="Branch Phone" value={branch.phone} />
                          <TableRow label="Full Address" value={branch.address} />
                        </tbody>
                      </table>
                    </div>
                    <div>
                      <table className="w-full border">
                        <tbody>
                          <TableRow label="Country" value={branch.country} />
                          <TableRow label="State" value={branch.state} />
                          <TableRow label="City" value={branch.city} />
                          <TableRow label="Pincode" value={branch.pincode} />
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Branch Contact Person */}
                  {branch.contactPerson && (
                    <div className="border-t pt-3">
                      <h6 className="font-semibold mb-2">Branch Contact Person</h6>
                      <table className="w-full border">
                        <tbody>
                          <TableRow label="Name" value={branch.contactPerson?.name} />
                          <TableRow label="Designation" value={branch.contactPerson?.designation} />
                          <TableRow label="Gender" value={branch.contactPerson?.gender} />
                          <TableRow label="Email" value={branch.contactPerson?.email} />
                          <TableRow label="Phone" value={branch.contactPerson?.phone} />
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ==================== DIAGNOSTICS - SIMPLIFIED SUMMARY ==================== */}
        {viewedCompany?.diagnostics?.length > 0 && (
          <div className="mb-8">
            <h4 className="text-lg font-semibold mb-3 border-b pb-2">
              Assigned Diagnostics ({viewedCompany.diagnostics.length})
            </h4>
            <div className="space-y-4">
              {viewedCompany.diagnostics.map((diag, idx) => (
                <div key={diag._id || idx} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                  {/* Diagnostic Header with Basic Info */}
                  <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                    <div className="flex-1">
                      <h5 className="font-semibold text-lg text-gray-800 mb-2">
                        {idx + 1}. {diag.name || 'Unnamed Diagnostic Center'}
                      </h5>
                    </div>

                    {/* Statistics Summary Cards */}
                    <div className="flex gap-2 flex-wrap md:flex-nowrap">
                      {/* Tests Summary */}
                      <div className="bg-white rounded-lg p-3 shadow-sm min-w-[100px] text-center border-l-4 border-blue-500">
                        <div className="text-2xl font-bold text-blue-600">{diag.tests?.length || 0}</div>
                        <div className="text-xs text-gray-600">Tests</div>
                        {diag.statistics?.testDistribution?.totalAssignments > 0 && (
                          <div className="text-[10px] text-gray-500 mt-1">
                            ({diag.statistics?.testDistribution?.totalAssignments || 0} assigned)
                          </div>
                        )}
                      </div>

                      {/* Packages Summary */}
                      <div className="bg-white rounded-lg p-3 shadow-sm min-w-[100px] text-center border-l-4 border-green-500">
                        <div className="text-2xl font-bold text-green-600">{diag.packages?.length || 0}</div>
                        <div className="text-xs text-gray-600">Packages</div>
                        {diag.statistics?.packageDistribution?.totalAssignments > 0 && (
                          <div className="text-[10px] text-gray-500 mt-1">
                            ({diag.statistics?.packageDistribution?.totalAssignments || 0} assigned)
                          </div>
                        )}
                      </div>

                      {/* Scans Summary */}
                      <div className="bg-white rounded-lg p-3 shadow-sm min-w-[100px] text-center border-l-4 border-purple-500">
                        <div className="text-2xl font-bold text-purple-600">{diag.scans?.length || 0}</div>
                        <div className="text-xs text-gray-600">Scans</div>
                        {diag.statistics?.scanDistribution?.totalAssignments > 0 && (
                          <div className="text-[10px] text-gray-500 mt-1">
                            ({diag.statistics?.scanDistribution?.totalAssignments || 0} assigned)
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* List of assigned items (just names, no details) */}
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Tests List */}
                    {diag.tests && diag.tests.length > 0 && (
                      <div>
                        <h6 className="text-sm font-semibold text-blue-700 mb-1">Tests:</h6>
                        <ul className="text-xs text-gray-600 list-disc list-inside">
                          {diag.tests.slice(0, 5).map((test, testIdx) => (
                            <li key={testIdx}>{test.testName || `Test ${testIdx + 1}`}</li>
                          ))}
                          {diag.tests.length > 5 && (
                            <li className="text-gray-500 italic">+{diag.tests.length - 5} more</li>
                          )}
                        </ul>
                      </div>
                    )}

                    {/* Packages List */}
                    {diag.packages && diag.packages.length > 0 && (
                      <div>
                        <h6 className="text-sm font-semibold text-green-700 mb-1">Packages:</h6>
                        <ul className="text-xs text-gray-600 list-disc list-inside">
                          {diag.packages.slice(0, 5).map((pkg, pkgIdx) => (
                            <li key={pkgIdx}>{pkg.packageDetails?.name || `Package ${pkgIdx + 1}`}</li>
                          ))}
                          {diag.packages.length > 5 && (
                            <li className="text-gray-500 italic">+{diag.packages.length - 5} more</li>
                          )}
                        </ul>
                      </div>
                    )}

                    {/* Scans List */}
                    {diag.scans && diag.scans.length > 0 && (
                      <div>
                        <h6 className="text-sm font-semibold text-purple-700 mb-1">Scans:</h6>
                        <ul className="text-xs text-gray-600 list-disc list-inside">
                          {diag.scans.slice(0, 5).map((scan, scanIdx) => (
                            <li key={scanIdx}>{scan.title || scan.scanName || `Scan ${scanIdx + 1}`}</li>
                          ))}
                          {diag.scans.length > 5 && (
                            <li className="text-gray-500 italic">+{diag.scans.length - 5} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Total Staff Count */}
                  {diag.statistics?.totalAssignedStaff > 0 && (
                    <div className="mt-2 text-xs text-right text-gray-600">
                      Total Staff Assigned: <span className="font-bold">{diag.statistics.totalAssignedStaff}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-end mt-6">
          <button
            onClick={closeViewModal}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  // ==================== MAIN RENDER ====================
  return (
    <div className="p-4 bg-white rounded shadow text-sm">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">Company List</h2>
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        <input
          type="text"
          className="px-2 py-1 border rounded text-sm w-64"
          placeholder="Search by company name..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
        />

        <CSVLink
          data={filteredCompanies}
          headers={headers}
          filename="company_list.csv"
          className="px-3 py-1 bg-green-500 text-white rounded flex items-center gap-1"
        >
          <FaFileCsv /> CSV
        </CSVLink>

        <label className="px-3 py-1 bg-blue-500 text-white rounded flex items-center gap-1 cursor-pointer">
          <FaUpload />
          Bulk Import
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleBulkImport}
            className="hidden"
          />
        </label>
      </div>

      <div className="overflow-x-auto">
        <div className="max-h-[400px] overflow-y-auto border rounded">
          <table className="w-full border-collapse text-xs">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="p-2 border">SI No</th>
                <th className="p-2 border">Name</th>
                <th className="p-2 border">Industry</th>
                <th className="p-2 border">Number of Branches</th>
                <th className="p-2 border">Empanelment</th>
                <th className="p-2 border">Contract Period</th>
                <th className="p-2 border">Renewal Date</th>
                <th className="p-2 border">Insurance Partner</th>
                <th className="p-2 border">Actions</th>
                <th className="p-2 border">View Employee</th>
                <th className="p-2 border">Add Beneficiary</th>
              </tr>
            </thead>
            <tbody>
              {currentCompanies.map((company, idx) => (
                <tr key={idx} className="hover:bg-green-100">
                  <td className="p-2 border text-center">{indexOfFirstCompany + idx + 1}</td>
                  <td className="p-2 border">{company.name}</td>
                  <td className="p-2 border">{company.companyType}</td>
                  <td className="p-2 border text-center">{company.noOfBranches}</td>
                  <td className="p-2 border">{company.registrationDate?.slice(0, 10)}</td>
                  <td className="p-2 border">{company.contractPeriod}</td>
                  <td className="p-2 border">{company.renewalDate?.slice(0, 10)}</td>
                  <td className="p-2 border">{company.insuranceBroker}</td>
                  <td className="p-2 border text-center">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => handleViewDetails(company._id)}
                        className="text-blue-500 hover:text-blue-700"
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                      <button
                        onClick={() => handleEdit(company._id)}
                        className="text-blue-500 hover:text-blue-700"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(company._id)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                  <td className="p-2 border text-center">
                    <button
                      onClick={() => handleViewStaff(company._id)}
                      className="px-2 py-1 bg-purple-900 text-white rounded text-xs"
                    >
                      View
                    </button>
                  </td>
                  <td className="p-2 border text-center">
                    <button
                      onClick={() => handleAddBeneficiary(company._id)}
                      className="px-2 py-1 bg-green-500 text-white rounded text-xs"
                    >
                      Add
                    </button>
                  </td>
                </tr>
              ))}
              {currentCompanies.length === 0 && (
                <tr>
                  <td colSpan={11} className="text-center p-3 text-gray-500">
                    No companies found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="mt-4 flex justify-center items-center gap-2 text-sm">
        <button
          onClick={prevPage}
          disabled={currentPage === 1}
          className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
        >
          Previous
        </button>
        {[...Array(totalPages)].map((_, idx) => (
          <button
            key={idx}
            onClick={() => paginate(idx + 1)}
            className={`px-3 py-1 rounded ${currentPage === idx + 1 ? "bg-blue-500 text-white" : "bg-gray-200"}`}
          >
            {idx + 1}
          </button>
        ))}
        <button
          onClick={nextPage}
          disabled={currentPage === totalPages}
          className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>

      {/* Edit Company Modal */}
      {isEditingCompanyModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-[95%] max-w-7xl max-h-[95vh] overflow-y-auto shadow-xl relative">
            <button className="absolute top-4 right-4 text-gray-500 hover:text-red-500 text-2xl font-bold" onClick={closeCompanyEditModal}>×</button>
            <h3 className="text-2xl font-bold mb-6 text-center text-blue-600">Edit Company</h3>

            <form onSubmit={handleSubmitEdit}>
              {/* Basic Information Section */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h4 className="text-lg font-semibold mb-4 text-gray-800">Basic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={companyDetails.name || ""}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, name: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Industry Type *</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={companyDetails.companyType || ""}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, companyType: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person *</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={companyDetails.assignedBy || ""}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, assignedBy: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Empanelment Date *</label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={companyDetails.registrationDate?.slice(0, 10) || ""}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, registrationDate: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contract Period *</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={companyDetails.contractPeriod || ""}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, contractPeriod: e.target.value })}
                      required
                    >
                      <option value="">Select Period</option>
                      <option value="1">1 Year</option>
                      <option value="2">2 Years</option>
                      <option value="3">3 Years</option>
                      <option value="4">4 Years</option>
                      <option value="5">5 Years</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Renewal Date *</label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={companyDetails.renewalDate?.slice(0, 10) || ""}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, renewalDate: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Partner *</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={companyDetails.insuranceBroker || ""}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, insuranceBroker: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={companyDetails.email || ""}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, email: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={companyDetails.phone || ""}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, phone: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={companyDetails.gstNumber || ""}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, gstNumber: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of Branches *</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={companyDetails.noOfBranches || ""}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, noOfBranches: e.target.value })}
                      required
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">EMP Head Count *</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={companyDetails.companyStrength || ""}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, companyStrength: e.target.value })}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Company Address Section */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h4 className="text-lg font-semibold mb-4 text-gray-800">Company Address</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={companyDetails.country || ""}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, country: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={companyDetails.state || ""}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, state: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={companyDetails.city || ""}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, city: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pincode *</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={companyDetails.pincode || ""}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, pincode: e.target.value })}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Contact Persons Section */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold text-gray-800">Contact Persons</h4>
                  <button
                    type="button"
                    onClick={() => {
                      const newContactPerson = {
                        name: '',
                        designation: '',
                        gender: '',
                        email: '',
                        phone: '',
                        address: {
                          country: '',
                          state: '',
                          city: '',
                          pincode: '',
                          street: ''
                        }
                      };
                      setCompanyDetails({
                        ...companyDetails,
                        contactPerson: [...(companyDetails.contactPerson || []), newContactPerson]
                      });
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    + Add Contact Person
                  </button>
                </div>

                <div className="space-y-4">
                  {companyDetails.contactPerson?.map((person, personIndex) => (
                    <div key={personIndex} className="border border-gray-300 rounded-lg p-4 bg-white">
                      <div className="flex justify-between items-center mb-3">
                        <h5 className="font-semibold text-gray-800">Contact Person {personIndex + 1}</h5>
                        <button
                          type="button"
                          onClick={() => {
                            const updatedContactPersons = [...companyDetails.contactPerson];
                            updatedContactPersons.splice(personIndex, 1);
                            setCompanyDetails({ ...companyDetails, contactPerson: updatedContactPersons });
                          }}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded"
                            value={person.name || ""}
                            onChange={(e) => {
                              const updatedContactPersons = [...companyDetails.contactPerson];
                              updatedContactPersons[personIndex].name = e.target.value;
                              setCompanyDetails({ ...companyDetails, contactPerson: updatedContactPersons });
                            }}
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Designation *</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded"
                            value={person.designation || ""}
                            onChange={(e) => {
                              const updatedContactPersons = [...companyDetails.contactPerson];
                              updatedContactPersons[personIndex].designation = e.target.value;
                              setCompanyDetails({ ...companyDetails, contactPerson: updatedContactPersons });
                            }}
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded"
                            value={person.gender || ""}
                            onChange={(e) => {
                              const updatedContactPersons = [...companyDetails.contactPerson];
                              updatedContactPersons[personIndex].gender = e.target.value;
                              setCompanyDetails({ ...companyDetails, contactPerson: updatedContactPersons });
                            }}
                            required
                          >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                          <input
                            type="email"
                            className="w-full px-3 py-2 border border-gray-300 rounded"
                            value={person.email || ""}
                            onChange={(e) => {
                              const updatedContactPersons = [...companyDetails.contactPerson];
                              updatedContactPersons[personIndex].email = e.target.value;
                              setCompanyDetails({ ...companyDetails, contactPerson: updatedContactPersons });
                            }}
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded"
                            value={person.phone || ""}
                            onChange={(e) => {
                              const updatedContactPersons = [...companyDetails.contactPerson];
                              updatedContactPersons[personIndex].phone = e.target.value;
                              setCompanyDetails({ ...companyDetails, contactPerson: updatedContactPersons });
                            }}
                            required
                          />
                        </div>
                      </div>

                      {/* Contact Person Address */}
                      <div className="mt-4 border-t pt-4">
                        <h6 className="font-semibold text-gray-700 mb-3">Address</h6>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded"
                              value={person.address?.country || ""}
                              onChange={(e) => {
                                const updatedContactPersons = [...companyDetails.contactPerson];
                                updatedContactPersons[personIndex].address.country = e.target.value;
                                setCompanyDetails({ ...companyDetails, contactPerson: updatedContactPersons });
                              }}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded"
                              value={person.address?.state || ""}
                              onChange={(e) => {
                                const updatedContactPersons = [...companyDetails.contactPerson];
                                updatedContactPersons[personIndex].address.state = e.target.value;
                                setCompanyDetails({ ...companyDetails, contactPerson: updatedContactPersons });
                              }}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded"
                              value={person.address?.city || ""}
                              onChange={(e) => {
                                const updatedContactPersons = [...companyDetails.contactPerson];
                                updatedContactPersons[personIndex].address.city = e.target.value;
                                setCompanyDetails({ ...companyDetails, contactPerson: updatedContactPersons });
                              }}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded"
                              value={person.address?.pincode || ""}
                              onChange={(e) => {
                                const updatedContactPersons = [...companyDetails.contactPerson];
                                updatedContactPersons[personIndex].address.pincode = e.target.value;
                                setCompanyDetails({ ...companyDetails, contactPerson: updatedContactPersons });
                              }}
                            />
                          </div>

                          <div className="md:col-span-2 lg:col-span-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Street</label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded"
                              value={person.address?.street || ""}
                              onChange={(e) => {
                                const updatedContactPersons = [...companyDetails.contactPerson];
                                updatedContactPersons[personIndex].address.street = e.target.value;
                                setCompanyDetails({ ...companyDetails, contactPerson: updatedContactPersons });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Branches Section */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold text-gray-800">Branches</h4>
                  <button
                    type="button"
                    onClick={() => {
                      const newBranch = {
                        branchName: '',
                        branchCode: '',
                        email: '',
                        phone: '',
                        branchHead: '',
                        country: '',
                        state: '',
                        city: '',
                        pincode: '',
                        address: '',
                        contactPerson: {
                          name: '',
                          designation: '',
                          email: '',
                          phone: '',
                          gender: ''
                        },
                        status: 'active'
                      };
                      setCompanyDetails({
                        ...companyDetails,
                        branches: [...(companyDetails.branches || []), newBranch]
                      });
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    + Add New Branch
                  </button>
                </div>

                <div className="space-y-6">
                  {companyDetails.branches?.map((branch, branchIndex) => (
                    <div key={branchIndex} className="border border-gray-300 rounded-lg p-6 bg-white">
                      <div className="flex justify-between items-center mb-4">
                        <h5 className="font-semibold text-lg text-gray-800">
                          Branch {branchIndex + 1}: {branch.branchName || 'New Branch'}
                        </h5>
                        <div className="flex gap-2">
                          <select
                            className="px-3 py-1 border border-gray-300 rounded text-sm"
                            value={branch.status || "active"}
                            onChange={(e) => {
                              const updatedBranches = [...companyDetails.branches];
                              updatedBranches[branchIndex].status = e.target.value;
                              setCompanyDetails({ ...companyDetails, branches: updatedBranches });
                            }}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>

                          <button
                            type="button"
                            onClick={() => {
                              const updatedBranches = [...companyDetails.branches];
                              updatedBranches.splice(branchIndex, 1);
                              setCompanyDetails({ ...companyDetails, branches: updatedBranches });
                            }}
                            className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      {/* Branch Basic Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Branch Name *</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded"
                            value={branch.branchName || ""}
                            onChange={(e) => {
                              const updatedBranches = [...companyDetails.branches];
                              updatedBranches[branchIndex].branchName = e.target.value;
                              setCompanyDetails({ ...companyDetails, branches: updatedBranches });
                            }}
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Branch Code *</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded"
                            value={branch.branchCode || ""}
                            onChange={(e) => {
                              const updatedBranches = [...companyDetails.branches];
                              updatedBranches[branchIndex].branchCode = e.target.value;
                              setCompanyDetails({ ...companyDetails, branches: updatedBranches });
                            }}
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Branch Head *</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded"
                            value={branch.branchHead || ""}
                            onChange={(e) => {
                              const updatedBranches = [...companyDetails.branches];
                              updatedBranches[branchIndex].branchHead = e.target.value;
                              setCompanyDetails({ ...companyDetails, branches: updatedBranches });
                            }}
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Branch Email *</label>
                          <input
                            type="email"
                            className="w-full px-3 py-2 border border-gray-300 rounded"
                            value={branch.email || ""}
                            onChange={(e) => {
                              const updatedBranches = [...companyDetails.branches];
                              updatedBranches[branchIndex].email = e.target.value;
                              setCompanyDetails({ ...companyDetails, branches: updatedBranches });
                            }}
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Branch Phone *</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded"
                            value={branch.phone || ""}
                            onChange={(e) => {
                              const updatedBranches = [...companyDetails.branches];
                              updatedBranches[branchIndex].phone = e.target.value;
                              setCompanyDetails({ ...companyDetails, branches: updatedBranches });
                            }}
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Full Address *</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded"
                            value={branch.address || ""}
                            onChange={(e) => {
                              const updatedBranches = [...companyDetails.branches];
                              updatedBranches[branchIndex].address = e.target.value;
                              setCompanyDetails({ ...companyDetails, branches: updatedBranches });
                            }}
                            required
                          />
                        </div>
                      </div>

                      {/* Branch Address */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded"
                            value={branch.country || ""}
                            onChange={(e) => {
                              const updatedBranches = [...companyDetails.branches];
                              updatedBranches[branchIndex].country = e.target.value;
                              setCompanyDetails({ ...companyDetails, branches: updatedBranches });
                            }}
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded"
                            value={branch.state || ""}
                            onChange={(e) => {
                              const updatedBranches = [...companyDetails.branches];
                              updatedBranches[branchIndex].state = e.target.value;
                              setCompanyDetails({ ...companyDetails, branches: updatedBranches });
                            }}
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded"
                            value={branch.city || ""}
                            onChange={(e) => {
                              const updatedBranches = [...companyDetails.branches];
                              updatedBranches[branchIndex].city = e.target.value;
                              setCompanyDetails({ ...companyDetails, branches: updatedBranches });
                            }}
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Pincode *</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded"
                            value={branch.pincode || ""}
                            onChange={(e) => {
                              const updatedBranches = [...companyDetails.branches];
                              updatedBranches[branchIndex].pincode = e.target.value;
                              setCompanyDetails({ ...companyDetails, branches: updatedBranches });
                            }}
                            required
                          />
                        </div>
                      </div>

                      {/* Branch Contact Person */}
                      <div className="border-t pt-4">
                        <h6 className="font-semibold text-gray-700 mb-3">Branch Contact Person</h6>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded"
                              value={branch.contactPerson?.name || ""}
                              onChange={(e) => {
                                const updatedBranches = [...companyDetails.branches];
                                updatedBranches[branchIndex].contactPerson.name = e.target.value;
                                setCompanyDetails({ ...companyDetails, branches: updatedBranches });
                              }}
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Designation *</label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded"
                              value={branch.contactPerson?.designation || ""}
                              onChange={(e) => {
                                const updatedBranches = [...companyDetails.branches];
                                updatedBranches[branchIndex].contactPerson.designation = e.target.value;
                                setCompanyDetails({ ...companyDetails, branches: updatedBranches });
                              }}
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                            <select
                              className="w-full px-3 py-2 border border-gray-300 rounded"
                              value={branch.contactPerson?.gender || ""}
                              onChange={(e) => {
                                const updatedBranches = [...companyDetails.branches];
                                updatedBranches[branchIndex].contactPerson.gender = e.target.value;
                                setCompanyDetails({ ...companyDetails, branches: updatedBranches });
                              }}
                              required
                            >
                              <option value="">Select Gender</option>
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                            <input
                              type="email"
                              className="w-full px-3 py-2 border border-gray-300 rounded"
                              value={branch.contactPerson?.email || ""}
                              onChange={(e) => {
                                const updatedBranches = [...companyDetails.branches];
                                updatedBranches[branchIndex].contactPerson.email = e.target.value;
                                setCompanyDetails({ ...companyDetails, branches: updatedBranches });
                              }}
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded"
                              value={branch.contactPerson?.phone || ""}
                              onChange={(e) => {
                                const updatedBranches = [...companyDetails.branches];
                                updatedBranches[branchIndex].contactPerson.phone = e.target.value;
                                setCompanyDetails({ ...companyDetails, branches: updatedBranches });
                              }}
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 mt-8">
                <button
                  type="button"
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                  onClick={closeCompanyEditModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Beneficiary Modal */}
      {isAddingBeneficiaryModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-[90%] max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl relative">
            <button className="absolute top-2 right-3 text-gray-500 hover:text-red-500 text-xl font-bold" onClick={closeBeneficiaryModal}>×</button>
            <StaffDetailsForm companyId={selectedCompanyId} closeModal={closeBeneficiaryModal} />
          </div>
        </div>
      )}

      {/* View Company Details Modal */}
      {isViewModalOpen && <ViewCompanyModal />}
    </div>
  );
};

export default CompanyList;